import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check localStorage for existing auth
    const storedAuth = localStorage.getItem('ele145_auth');
    if (storedAuth) {
      try {
        const authData = JSON.parse(storedAuth);
        if (authData.loggedIn) {
          setUser(authData);
        }
      } catch (e) {
        localStorage.removeItem('ele145_auth');
      }
    }
    setLoading(false);
  }, []);

  const login = (email, password, role = 'user', userData = null) => {
    const authData = {
      email,
      name: userData?.name || email.split('@')[0],
      role: role.toLowerCase() === 'admin' ? 'admin' : 'user',
      extension: userData?.extension || userData?.id || '',
      department: userData?.department || '',
      loggedIn: true,
      loginTime: new Date().toISOString()
    };
    localStorage.setItem('ele145_auth', JSON.stringify(authData));
    setUser(authData);
    return authData;
  };

  const logout = () => {
    localStorage.removeItem('ele145_auth');
    setUser(null);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isAuthenticated = () => {
    return user !== null && user.loggedIn;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAdmin, isAuthenticated, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
export const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated()) {
        navigate('/login', { state: { from: location.pathname } });
      } else if (adminOnly && !isAdmin()) {
        navigate('/', { state: { error: 'Access denied. Admin only.' } });
      }
    }
  }, [loading, user, adminOnly, navigate, location, isAuthenticated, isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return null;
  }

  if (adminOnly && !isAdmin()) {
    return null;
  }

  return children;
};

export default AuthContext;
